import {Component, Signal} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {Organization} from '../../types-repository';
import {Airline, CodecType, Mutation, StartWorkflowDefaultInput, TrackInput, WorkflowProcessPayload} from '../../types-workflow';
import {clean_datetime} from '../utils';
import {customQuote, PO_NAME_MAX_LENGTH, s3TagValueLengthValidator, S3_TAG_VALUE_MAX_LENGTH} from '../name-limits';
import {ALL_SYSTEMS, IfeFormat, PoDestination} from '../../target_systems';
import {OAuthService} from 'angular-oauth2-oidc';
import {START_WORKFLOW_DEFAULT} from '../../queries-workflow';
import {catchError, distinct, map, mergeMap, retry, tap, toArray} from 'rxjs/operators';
import {Apollo} from 'apollo-angular';
import {from, merge, Observable, of} from 'rxjs';
import {DoButtonComponent} from '../do-button/do-button.component';
import {ProcessService} from '../../services/process.service';
import {LogoAreaComponent} from '../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';

import {ContextService} from '../../services/context.service';
import {Dialog} from '@angular/cdk/dialog';
import {ErrorDialogComponent} from '../../error-dialog/error-dialog.component';
import {reportInvalid} from '../submit-po/form-messages';
import {FormFooterComponent} from '../form-footer/form-footer.component';
import {Router, RouterModule} from '@angular/router';


function normalize(v: unknown): string {

  return (v ?? '').toString().trim();
}


export function uniquePerControlInArrayValidator(): ValidatorFn {
  return (ctrl: AbstractControl): ValidationErrors | null => {
    if (!(ctrl instanceof FormArray)) {
      return null;
    }

    const fa = ctrl as FormArray<FormControl>;
    const values = fa.controls.map(c => {

      return (c as unknown as FormGroup).controls['id'] ? normalize((c as unknown as FormGroup).controls['id'].value) : null;
    });

    const occurrences = new Map<string, number[]>();
    values.forEach((v, i) => {
      if (!v) {
        return;
      }
      const arr = occurrences.get(v) ?? [];
      arr.push(i);
      occurrences.set(v, arr);
    });

    const duplicateIdx = new Set<number>();
    occurrences.forEach(idxs => {
      if (idxs.length > 1) {
        idxs.forEach(i => duplicateIdx.add(i));
      }
    });

    fa.controls.forEach((c, i) => {
      const idControl = (c as unknown as FormGroup).controls['id'];
      const existing = idControl.errors ?? {};
      const {notUnique, ...rest} = existing;  // "notUnique" rauslösen

      if (duplicateIdx.has(i)) {
        const next = {...rest, notUnique: [...duplicateIdx].map(j => j + 1).join(', ')};
        if (!shallowEqual(existing, next)) {
          idControl.setErrors(next);
        }
      } else {
        const next = Object.keys(rest).length ? rest : null;
        if (!shallowEqual(existing, next)) {
          idControl.setErrors(next);
        }
      }
    });

    return duplicateIdx.size ? {notUnique: true} : null;
  };
}

function shallowEqual(a: any, b: any) {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) {
    return false;
  }
  return ak.every(k => a[k] === b[k]);
}

export function compoundFieldValidator(regex: RegExp, dummyValue: string): ValidatorFn {
  return (control: AbstractControl) => {
    if (!control.value) {
      return null;
    } // No error if empty

    // Modify the value before validation
    const modifiedValue = control.value + dummyValue;

    // Run regex validation
    return regex.test(modifiedValue) ? null : {invalidFormat: true};
  };
}

/** The `po_name` submitted for a track — the single place this string is composed. */
export function composePoName(airline: unknown, poNumber: unknown, album: unknown): string {
  return `${(airline ?? '').toString()}_${(poNumber ?? '').toString().trim()}/${(album ?? '').toString()}`;
}

/**
 * The album field is not tagged on its own: it is the tail of `po_name`, and `po_name` is what gets
 * `custom_quote`d into an S3 object tag alongside `po_item_id`. So the album is validated through
 * the full name it produces — airline, PO number and album together.
 */
export function poNameS3TagValueLengthValidator(max: number = S3_TAG_VALUE_MAX_LENGTH): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const formGroup = control.parent?.parent?.parent;
    if (!formGroup) {
      return null;
    }

    const poName = composePoName(
      formGroup.get('airline')?.value,
      formGroup.get('po_number')?.value,
      control.value
    );
    const encodedLength = customQuote(poName).length;
    return encodedLength > max
      ? {s3TagValueTooLong: {max, encoded: encodedLength, actual: poName.length}}
      : null;
  };
}

/**
 * VTK's job bundle limit applies to the whole po_name, so the album is checked through the name it
 * produces — airline, PO number and album together, exactly as submitted.
 */
export function maxPoNameLengthValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const formGroup = control.parent?.parent?.parent;
    if (!formGroup) { return null; }

    const poName = composePoName(formGroup.get('airline')?.value, formGroup.get('po_number')?.value, control.value);
    return poName.length > PO_NAME_MAX_LENGTH
      ? { maxPoNameLengthValidator: { max: PO_NAME_MAX_LENGTH, actual: poName.length } }
      : null;
  };
}

@Component({
  selector: 'app-order-music',
  templateUrl: './audio-order.component.html',
  styleUrl: './audio-order.component.scss',
  imports: [
    FormFooterComponent,
    ReactiveFormsModule,
    LogoAreaComponent,
    HeadlineSectionComponent,
    RouterModule
],
})
export class AudioOrderComponent {
  organization: Signal<Organization>;


  airlines: Signal<Airline[]>;

  availableSystems = []

  ifeFormats: IfeFormat[] = []
  poDestinations: PoDestination[] = []
  poFormGroup: FormGroup;
  ifeFormat: IfeFormat;
  started = false;

  constructor(
    private formBuilder: FormBuilder,
    oAuthService: OAuthService,
    private apollo: Apollo,
    private processService: ProcessService,
    contextService: ContextService,
    private dialog: Dialog,
    private router: Router
  ) {

    this.airlines = contextService.airlines;
    this.organization = contextService.organization;

    this.availableSystems = ALL_SYSTEMS.map(system => ({
      ...system,
      formats: system.formats
    }));
    this.poFormGroup = formBuilder.group({
      po_number: [clean_datetime(new Date()), Validators.required],
      airline: [null, Validators.required],
      // Required: start_workflow_default takes po_destination as String!; every format offers one.
      po_destination: [null, Validators.required],
      auto_publish: [false],
      due_date: ['now'],
      poItems: this.formBuilder.array([], uniquePerControlInArrayValidator()),
      date_created: [null],
      email_notification: [null, [Validators.required, Validators.email]],
      email_notification_publish: [null, [Validators.email]],
      ife: [null, Validators.required],
      ife_format: [null, Validators.required],
      idType: ['isrc', Validators.required],
    });
    (this.poFormGroup.controls['poItems'] as unknown as FormArray).push(this.createPoItem('', '', '', ''));

    this.poFormGroup.controls['ife'].valueChanges.pipe(distinct(v =>  v?.item_id)).subscribe(value => {
      this.ifeFormats = value.formats;
      this.ifeFormat = value.formats[0];
      this.poFormGroup.controls['ife_format'].setValue(value.formats[0]);
    })

    this.poFormGroup.controls['idType'].valueChanges.subscribe(value => {
      this.poItems.controls.forEach(poItem => {
        this.setPoItemValidators(poItem as FormGroup);
      });
    })

    // The album's length limits are on the composed po_name, so they change with airline / PO number.
    merge(
      this.poFormGroup.controls['airline'].valueChanges,
      this.poFormGroup.controls['po_number'].valueChanges
    ).subscribe(() => {
      this.poItems.controls.forEach(poItem => {
        (poItem as FormGroup).controls['album'].updateValueAndValidity();
      });
    })

    this.poFormGroup.controls['ife_format'].valueChanges.pipe(distinct(v => v?.item_id)).subscribe(value => {
      this.ifeFormat = value;
      this.poDestinations = this.ifeFormat?.po_destinations || [];

      // Preserve user's destination selection if it's still valid for the new format
      const currentDest = this.poFormGroup.controls['po_destination'].value;
      const stillValid = this.poDestinations.find(d => d.destination_id === currentDest?.destination_id);
      this.poFormGroup.controls['po_destination'].setValue(stillValid || this.poDestinations[0]);

      if (this.ifeFormat?.po_pattern) {
        this.poFormGroup.controls['po_number'].setValidators([Validators.required, compoundFieldValidator(this.ifeFormat.po_pattern.pattern, '/aaa')]);
        this.poFormGroup.controls['po_number'].updateValueAndValidity();
      } else {
        this.poFormGroup.controls['po_number'].setValidators([Validators.required])

      }
    })
    // todo retrieve email from user profile
    this.poFormGroup.controls['email_notification'].setValue(oAuthService.getIdentityClaims()['email']);
    this.poFormGroup.controls['email_notification_publish'].setValue(oAuthService.getIdentityClaims()['email']);

  }


  setPoItemValidators(poItem: FormGroup) {
    // The album is the tail of po_name, which is custom_quote-encoded into an S3 object tag.
    poItem.controls['album'].setValidators([maxPoNameLengthValidator(), poNameS3TagValueLengthValidator()]);
    poItem.controls['album'].updateValueAndValidity();
    // The track name is submitted as po_item_id and ends up custom_quote-encoded in an S3 object tag.
    poItem.controls['name'].setValidators([s3TagValueLengthValidator()]);
    poItem.controls['name'].updateValueAndValidity();
    if (this.poFormGroup.controls['idType'].value === 'isrc') {
      poItem.controls['id'].setValidators([Validators.pattern(/^[A-Z]{2}-?[A-Z0-9]{3}-?[0-9]{2}-?[0-9]{5}$/)]);
    } else if (this.poFormGroup.controls['idType'].value === 's3') {
      poItem.controls['id'].setValidators([Validators.pattern(/^s3:\/\/[^"\\\r\n]+$/)]);
    } else {
      poItem.controls['id'].setValidators([])
    }
    poItem.controls['id'].updateValueAndValidity();
  }

  createPoItem(album, id, name, filename): FormGroup {
    console.log('createPoItem', id, name, filename)

    const poItem = this.formBuilder.group({
      album: [album],
      id: [id] ,
      name: [name],
      filename: [filename],
      submitted: [false]
    });

    this.setPoItemValidators(poItem);
    return poItem
  }

  onInputChange(index: number): void {
    const poItems = this.poItems;
    const lastItem = poItems.at(index);
    if (lastItem.get('id')?.value && lastItem.get('name')?.value && lastItem.get('filename')?.value && index === poItems.length - 1) {
      poItems.push(this.createPoItem('', '', '', ''));
    }
  }

  get poItems(): FormArray {
    return this.poFormGroup.get('poItems') as FormArray;
  }

  onPaste(event: ClipboardEvent, index: number): void {
    const clipboardData = event.clipboardData?.getData('text') || '';
    const rows = clipboardData.split('\n').filter(row => row.trim() !== ''); // Filter out empty rows

    // Check if the clipboard data is in valid CSV/tab-separated format
    const isCSV = rows.some(row => row.includes('\t'));
    if (!isCSV) {
      console.error('Invalid paste format. Expected tab-separated values.');
      return;
    }

    event.preventDefault(); // Prevent default paste behavior

    rows.forEach((row, rowIndex) => {
      const [album, name, id, filename] = row.split('\t').map(item => item.trim()); // Map to remove unnecessary spaces
      if (album && id && filename && name) {
        if (rowIndex === 0) {
          // Update the current row
          this.poItems.at(index).patchValue({'album': album, 'id': id, 'name': name, 'filename': filename});
        } else {
          // Add new rows
          this.poItems.push(this.createPoItem(album, id, name, filename));
        }
      }
    });

    // Ensure there's an empty row at the end for new entries
    const lastItem = this.poItems.at(this.poItems.length - 1);
    if (lastItem.get('id')?.value || lastItem.get('name')?.value || lastItem.get('filename')?.value) {
      this.poItems.push(this.createPoItem('', '', '', ''));
    }
  }

  compare_item_id_system(o1: any, o2: any) {
    console.log('compare_item_id_system', o1, o2);
    return o1?.item_id === o2?.item_id;
  }
  compare_item_id_format(o1: any, o2: any) {
    console.log('compare_item_id_format', o1, o2);
    return o1?.item_id === o2?.item_id;
  }

  compare_destination_id(o1: any, o2: any) {
    console.log('compare_destination_id', o1, o2);
    return o1?.destination_id === o2?.destination_id;
  }

  submitPo(
    button: DoButtonComponent
  ) {
    if (!this.poFormGroup.valid) {
      reportInvalid(button, this.poFormGroup, 'audio-order-form');
      this.poFormGroup.markAllAsTouched();
      return
    }
    const noOfItems = this.poItems.value.filter(item => item.id !== '' && item.filename !== '').length
    let noOfItemsSubmitted = 0
    const errors: string[] = []
    button.info(`Submitting ${noOfItems} tracks`);
    this.poFormGroup.disable()
    this.poItems.disable()

    const workflows: (Observable<{
      process: WorkflowProcessPayload
      trackId: any
    }> | null)[] = []
    const emails = this.poFormGroup.controls['email_notification'].value.split(',').map(e => e.trim()).filter(e => e !== '')
    const emailsPublish = this.poFormGroup.controls['email_notification_publish'].value?.split(',').map(e => e.trim()).filter(e => e !== '') || []
    this.poFormGroup.updateValueAndValidity();
    for (const trackControl of this.poItems.controls) {
      const content_type = this.ifeFormat.audio_only ? 'AUDIO_RECORDING' : 'MUSIC_VIDEO'
      const track = trackControl.value;
      if (track.id !== '' && track.filename !== '') {
        let contentIdentifier = track.id
        if (this.poFormGroup.controls['idType'].value === 'isrc') {
          contentIdentifier = `isrc://${contentIdentifier}?contentType=${content_type}`
        } else if (this.poFormGroup.controls['idType'].value === 's3') {
          if (!track.id.startsWith('s3://')) {
            contentIdentifier = `s3://${contentIdentifier}`
          }
        } else {
          console.warn('Unknown idType', this.poFormGroup['idType'], 'using id as content identifier');
        }
        const tracks: TrackInput[] = [{
          'codec_type': CodecType.Audio,
          'lang': '*'
        }]
        if (!this.ifeFormat.audio_only) {
          tracks.push({
            'codec_type': CodecType.Video
          })
        }
        const poDestValue = this.poFormGroup.controls['po_destination'].value;
        const poDestId = poDestValue?.destination_id;
        console.log('Submitting workflow with po_destination:', poDestValue, 'extracted destination_id:', poDestId);

        const workflow$ = this.apollo.use('workflow').mutate<Mutation>({
          mutation: START_WORKFLOW_DEFAULT,
          variables: {
            email_notification: emails,
            email_notification_publish: emailsPublish.length > 0 ? emailsPublish : null,
            auto_publish: this.poFormGroup.controls['auto_publish'].value,
            aspect_ratio: null,
            filename: track.filename,
            metadata_source: null,
            wrap_up_workflow: this.ifeFormat.wrap_up_workflow,
            content_identifier: contentIdentifier,
            po_item_id: track.name,
            po_name: composePoName(
              this.poFormGroup.controls['airline'].value,
              this.poFormGroup.controls['po_number'].value,
              track.album
            ),
            po_destination: poDestId,
            vtk_template: this.ifeFormat.vtk_template,
            format_specific_data: '{}',
            watermark: false,
            tracks: tracks
          } satisfies StartWorkflowDefaultInput
        }).pipe(
          retry(5),
          map(d => ({
            process: d.data.start_workflow_default,
            trackId: track.id
          })),
          catchError(err => {
            errors.push(`${track.album}\t${track.id}\t${track.name}\t${track.filename}\t${err.message}`);
            return of(null);
          }),
          tap(workflow => {
            trackControl.get('submitted')?.setValue(true);
            noOfItemsSubmitted += 1;
            button.info(`Submitted ${noOfItemsSubmitted}/${noOfItems} tracks`, {
              duration: -1,
            });

          }),
        );
        workflows.push(workflow$);
      }
    }
    if (workflows.length > 0) {
      from(workflows).pipe(
        mergeMap(obs => obs, 5), // Only 5 concurrent submissions
        toArray() // Gather results into an array once all are done
      ).subscribe({
        next: (_workflows) => {
          if (errors.length === 0) {
            button.done(`${noOfItemsSubmitted} workflows started successfully.`, {duration: -1});
            this.started = true;
          } else {
            button.done(`${errors.length} workflows failed to start.`, {severity: 'cl-alert-warning', duration: -1});
            this.dialog.open(ErrorDialogComponent, {
              data: {
                messagge: 'These workflows failed to start:',
                messages: errors,
                button: 'OK',
              },
            })
          }

          this.poFormGroup.enable();
          this.poItems.enable();

          _workflows
            .filter(wf => wf !== null) // only non-failed
            .forEach(workflow => {
              this.processService.observeProcess(workflow.process, {
                successMessage: `${workflow.trackId} encoded.`,
                failureMessage: `Failed to encode ${workflow.trackId}.`,
                link: ['/#/o', this.organization()?.id, 'workflows',
                  this.poFormGroup.value.airline,
                  `${this.poFormGroup.value.airline}_${this.poFormGroup.value.po_number.trim()}`
                ].join('/')
              });
            });
        },
        error: (err) => {
          button.done('Error starting workflows.', {severity: 'cl-alert-danger', duration: -1});
          console.error(err);
          this.poFormGroup.enable();
          this.poItems.enable();
        }
      });
    } else {
      button.done('No valid workflows to start.', {severity: 'cl-alert-warning', duration: -1});
      this.poFormGroup.enable();
      this.poItems.enable();
    }
  }

  reset(button: DoButtonComponent) {
    button.info('Reset form', {duration: 300});
    this.poFormGroup.controls['poItems'] = this.formBuilder.array([this.createPoItem('', '', '', '')]);
  }

  openAll() {
    const urls: string[] = []
    const pos = new Set()
    for (const poItem of this.poItems.controls) {
      if ((poItem as unknown as FormGroup).controls['album'].value !== '') {
        const po = composePoName(
          this.poFormGroup.controls['airline'].value,
          this.poFormGroup.controls['po_number'].value,
          (poItem as unknown as FormGroup).controls['album'].value
        )
        if (pos.has(po)) {
          continue; // Skip duplicates
        }
        pos.add(po);
        urls.push(
          this.router.serializeUrl(
            this.router.createUrlTree(
              ['/o', this.organization()?.id,
                'workflows',
                this.poFormGroup.controls['airline'].value,
                po
              ],
            )
          ));
      }
    }
    console.log(urls);
    for (const url of urls) {
      window.open( '/#/' + url, '_blank', 'noopener,noreferrer');
    }
  }
}
